import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useLoginPlaymove } from "../../Hooks";
import { useTranslation } from "react-i18next";
import { passwordRegex, region } from "../../Configs";
import { useUserApi } from "../../Hooks";

import header from "../../assets/img/cabecalho-login.png";
import footer from "../../assets/img/footer-login.png";
import ForgotPasswordModal from "../Login/ForgotPasswordModal";
import LanguageSelector from "../Shared/LanguageSelector";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { getInitialLanguage } from "../../Utils/getInitialLanguage";
import { getLocalizedRoutePath, ROUTE_KEYS } from "../../routes/definitions";

export default function LoginPlaymove() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const userApi = useUserApi();

  // Estados para login e atualização de senha
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [alert, setAlert] = useState({ type: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [updatePassword, setUpdatePassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState("");
  const [passwordError, setPasswordError] = useState({ confirmation: false, regex: false });
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const { tokenExists, login, clearTokens } = useLoginPlaymove();

  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const acessoPlataformasPath = useMemo(() => getLocalizedRoutePath(ROUTE_KEYS.PLATFORM_ACCESS, i18n.language), [i18n.language]);

  // Configuração do idioma
  const [language, setLanguage] = useState(getInitialLanguage());
  useEffect(() => {
    setLanguage(getInitialLanguage());
    i18n.changeLanguage(getInitialLanguage());
  }, [i18n]);

  useEffect(() => {
    if (tokenExists() && !updatePassword) {
      navigate(acessoPlataformasPath);
    }
  }, [navigate, tokenExists, updatePassword, acessoPlataformasPath]);

  // Função de submit do formulário de login
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setAlert({ type: "", message: "" });
    try {
      const result = await login(email, password, rememberMe);
      if (result.success) {
        if (result.shouldUpdatePassword) {
          // Se o usuário precisar atualizar a senha, não redireciona
          setUpdatePassword(true);
        } else {
          navigate(acessoPlataformasPath);
        }
      } else {
        setAlert({ type: "error", message: t("invalidLoginError") });
      }
    } catch (error) {
      console.log(error);
      setAlert({ type: "error", message: t("genericLoginError") });
    }
    setLoading(false);
  };

  // Função de submit para atualização de senha
  const handleSubmitNewPassword = async (e) => {
    e.preventDefault();
    setPasswordError({ confirmation: false, regex: false });
    let tempError = { confirmation: false, regex: false };

    if (!passwordRegex.test(newPassword)) {
      tempError.regex = true;
    } else if (newPassword !== newPasswordConfirmation) {
      tempError.confirmation = true;
    }

    clearTokens();

    if (!tempError.regex && !tempError.confirmation) {
      const updateResult = await userApi.updateUserPassword(email, password, newPassword);
      if (updateResult.status === 200) {
        const doLogin = await login(email, newPassword, false);
        if (doLogin.success) {
          navigate(acessoPlataformasPath);
        } else {
          setAlert({ type: "error", message: t("invalidLoginError") });
        }
      } else {
        tempError.regex = true;
      }
    }
    setPasswordError(tempError);
  };

  const handleForgotPasswordClick = () => {
    setShowForgotPasswordModal(true);
  };

  return (
    <div className="h-screen w-full flex flex-col overflow-y-auto md:overflow-y-hidden text-sm md:text-base">
      {/* HEADER */}
      <div className="w-full flex-shrink-0 h-auto md:h-[15%]">
        <img
          src={header}
          alt={t("headerAlt")}
          className="w-full h-auto md:h-full object-contain md:object-cover md:w-full"
          style={{ maxWidth: "100%" }}
        />
      </div>

      {/* CONTEÚDO */}
      <div className="w-full h-auto md:h-[65%] flex flex-col items-center justify-center px-4 gap-4">
        <h1 className="text-xl md:text-2xl font-bold text-gray-800 text-center">
          {t("welcome")}
        </h1>
        <p className="text-base md:text-lg text-gray-600 text-center">
          {t("loginSubtitle")}
        </p>

        {/* Card de Login / Atualização de Senha */}
        <div className="bg-[#F5F9FF] w-full max-w-md p-6 rounded shadow">
          {updatePassword ? (
            <div>
              <h2 className="text-xl font-bold text-gray-800 text-center mb-2">
                {t("updatePasswordTitle")}
              </h2>
              <p className="text-base text-gray-600 text-center mb-4">
                {t("updatePasswordSubtitle")}
              </p>
              <form onSubmit={handleSubmitNewPassword}>
                <div className="relative mt-1 mb-4">
                  <input
                    id="newPassword"
                    type={showNewPassword ? "text" : "password"}
                    autoComplete="new-password"
                    className="w-full p-2 border rounded focus:outline-none"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                  <span
                    className="absolute right-3 top-[50%] transform -translate-y-1/2 cursor-pointer"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
                  </span>
                </div>

                <label htmlFor="newPasswordConfirmation" className="block font-semibold text-gray-700">
                  {t("confirmNewPasswordLabel")}
                </label>
                <div className="relative mt-1 mb-4">
                  <input
                    id="newPasswordConfirmation"
                    type={showConfirmPassword ? "text" : "password"}
                    autoComplete="new-password"
                    className="w-full p-2 border rounded focus:outline-none"
                    value={newPasswordConfirmation}
                    onChange={(e) => setNewPasswordConfirmation(e.target.value)}
                    required
                  />
                  <span
                    className="absolute right-3 top-[50%] transform -translate-y-1/2 cursor-pointer"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
                  </span>
                </div>
                {passwordError.regex && (
                  <p className="text-red-500 text-sm mb-2">
                    {t("updatePasswordErrorRegex")}
                  </p>
                )}
                {passwordError.confirmation && (
                  <p className="text-red-500 text-sm mb-2">
                    {t("updatePasswordErrorConfirmation")}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 rounded-full bg-blue-600 text-white text-lg font-bold transition-all"
                >
                  {loading ? t("updatePasswordButtonLoading") : t("updatePasswordButtonConfirm")}
                </button>
              </form>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <label htmlFor="email" className="block font-semibold text-gray-700">
                {t("email")}
              </label>
              <input
                id="email"
                type="email"
                className={`w-full mt-1 mb-4 p-2 border rounded ${alert.type === "error" ? "border-red-400" : "border-gray-300"} focus:outline-none`}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onClick={() => setAlert({ type: "", message: "" })}
                required
              />

              <label htmlFor="password" className="block font-semibold text-gray-700">
                {t("password")}
              </label>
              <div className="relative mt-1 mb-4">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  className={`w-full p-2 border rounded ${alert.type === "error" ? "border-red-400" : "border-gray-300"} focus:outline-none`}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onClick={() => setAlert({ type: "", message: "" })}
                  required
                />
                <span
                  className="absolute right-3 top-[50%] transform -translate-y-1/2 cursor-pointer"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
                </span>
              </div>

              <div className="flex items-center mb-4">
                <div className="flex items-center">
                  <input
                    id="rememberMe"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 text-blue-600"
                  />
                  <label htmlFor="rememberMe" className="ml-2 text-gray-700">
                    {t("rememberMe")}
                  </label>
                </div>
                <button
                  type="button"
                  onClick={handleForgotPasswordClick}
                  className="ml-auto text-blue-600 hover:underline"
                >
                  {t("forgotPassword")}
                </button>
              </div>

              {alert.type && (
                <div
                  className={`text-sm p-2 rounded mb-2 ${alert.type === "error"
                    ? "bg-red-100 text-red-700"
                    : "bg-green-100 text-green-700"
                    }`}
                >
                  {alert.message}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className={`w-full py-3 rounded-full bg-blue-600 text-white text-lg font-bold transition-all ${loading ? "opacity-70 cursor-not-allowed" : "hover:bg-blue-700"
                  }`}
              >
                {loading ? t("signingIn") : t("loginButton")}
              </button>
            </form>
          )}
          <ForgotPasswordModal
            isOpen={showForgotPasswordModal}
            onClose={() => setShowForgotPasswordModal(false)}
            language={language}
            isLegacyTemplate={false}
          />
        </div>
      </div>

      {/* FOOTER */}
      <div className="w-full flex-shrink-0 h-auto md:h-[10%]">
        <div className="flex justify-center items-center">
          <img src={footer} alt={t("footerAlt")} />
        </div>
      </div>
      {localStorage.getItem("language") === "pt-BR" && (
        <div className="fixed bottom-5 left-4">
          <a
            href="https://playmove.marketingautomation.services/prospector/previewPages/MzawMDEzt7QwAgA/200000000003952"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-full shadow-lg text-lg"
          >
            Acesse o Manual e outros materiais
          </a>
        </div>
      )}

      {/* Seletor de Idioma */}
      <LanguageSelector />
    </div>
  );
}
